import React, { useState, useEffect } from "react";
import { Bold, Italic, Underline, List, ArrowUp, ArrowDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { question: "", answer: "", displayOrder: 0 };

function RichTextToolbar({ field, insertFormatting }) {
  return (
    <div
      className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50"
      onMouseDown={(e) => e.preventDefault()}
    >
      <button type="button" onClick={() => insertFormatting(field, "bold")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
      <button type="button" onClick={() => insertFormatting(field, "italic")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
      <button type="button" onClick={() => insertFormatting(field, "underline")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
      <button type="button" onClick={() => insertFormatting(field, "list")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
      <div style={{ width: 1, height: 14, background: "#e5e7eb", margin: "0 2px" }} />
      <select
        onChange={(e) => { if (e.target.value) { insertFormatting(field, e.target.value); e.target.value = ""; } }}
        onMouseDown={(e) => e.stopPropagation()}
        className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none"
        style={{ height: 22 }}
      >
        <option value="">Normal</option>
        <option value="size-10">10px</option>
        <option value="size-11">11px</option>
        <option value="size-12">12px</option>
        <option value="size-13">13px (small)</option>
        <option value="size-14">14px (default)</option>
        <option value="size-16">16px</option>
        <option value="size-18">18px</option>
        <option value="size-24">24px (large)</option>
      </select>
    </div>
  );
}

export default function FaqAdmin() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faq`);
      if (res.data.success) setFaqs(res.data.data);
    } catch {
      setError("Failed to load FAQ items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const openAdd = () => {
    const nextOrder = faqs.length
      ? Math.max(...faqs.map((f) => f.displayOrder ?? 0)) + 1
      : 0;
    setForm({ ...EMPTY_FORM, displayOrder: nextOrder });
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = (f) => {
    setForm({ question: f.question || "", answer: f.answer || "", displayOrder: f.displayOrder ?? 0 });
    setEditingId(f.id);
    setShowModal(true);
  };

  const insertFormatting = (field, format) => {
    const textarea = document.querySelector(`textarea[name="${field}"]`);
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const val = textarea.value;
    const selected = val.substring(start, end);
    let newText = val;
    let newCursor = start;

    if (format === "bold") {
      const t = selected || "bold text";
      newText = val.substring(0, start) + `**${t}**` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === "italic") {
      const t = selected || "italic text";
      newText = val.substring(0, start) + `*${t}*` + val.substring(end);
      newCursor = start + 1 + t.length + 1;
    } else if (format === "underline") {
      const t = selected || "underlined text";
      newText = val.substring(0, start) + `__${t}__` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === "list") {
      const t = selected || "list item";
      newText = val.substring(0, start) + `\n- ${t}` + val.substring(end);
      newCursor = start + 3 + t.length;
    } else if (format.startsWith("size-")) {
      const px = format.split("-")[1];
      const t = selected || "text";
      const open = `<span style="font-size:${px}px;color:inherit">`;
      newText = val.substring(0, start) + open + t + `</span>` + val.substring(end);
      newCursor = start + open.length + t.length + 7;
    } else {
      return;
    }

    setForm((prev) => ({ ...prev, [field]: newText }));
    setTimeout(() => { textarea.setSelectionRange(newCursor, newCursor); textarea.focus(); }, 0);
  };

  const handleRichTextKeyDown = (field, e) => {
    if (e.key !== "Enter") return;
    const textarea = e.target;
    const pos = textarea.selectionStart;
    const val = textarea.value;
    const lineStart = val.lastIndexOf("\n", pos - 1) + 1;
    const currentLine = val.substring(lineStart, pos);
    if (!currentLine.startsWith("- ")) return;
    e.preventDefault();
    if (currentLine === "- ") {
      const newVal = val.substring(0, lineStart) + val.substring(pos);
      setForm((prev) => ({ ...prev, [field]: newVal }));
      setTimeout(() => textarea.setSelectionRange(lineStart, lineStart), 0);
    } else {
      const newVal = val.substring(0, pos) + "\n- " + val.substring(pos);
      setForm((prev) => ({ ...prev, [field]: newVal }));
      setTimeout(() => { const np = pos + 3; textarea.setSelectionRange(np, np); }, 0);
    }
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      alert("Question and answer are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, displayOrder: Number(form.displayOrder) };
      if (editingId) {
        await axios.put(`${API}/admin/faq/${editingId}`, payload);
      } else {
        await axios.post(`${API}/admin/faq`, payload);
      }
      setShowModal(false);
      fetchFaqs();
    } catch {
      alert("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (id, direction) => {
    try {
      await axios.patch(`${API}/admin/faq/${id}/move`, { direction });
      fetchFaqs();
    } catch {
      alert("Reorder failed.");
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) { setDeleteConfirm(id); return; }
    try {
      await axios.delete(`${API}/admin/faq/${id}`);
      setDeleteConfirm(null);
      fetchFaqs();
    } catch {
      alert("Delete failed.");
    }
  };

  const labelStyle = {
    display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600,
    color: "#374151", fontFamily: "Montserrat, sans-serif",
    textTransform: "uppercase", letterSpacing: "0.5px",
  };
  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB",
    borderRadius: 7, fontSize: 14, fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box", color: "#111827", background: "#fff",
  };
  const thStyle = {
    padding: "10px 14px", textAlign: "left",
    fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12,
    color: "#6B7280", borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "12px 14px", borderBottom: "1px solid #F3F4F6", verticalAlign: "middle",
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 20, color: "#111827", margin: 0 }}>
            FAQ
          </h2>
          <p style={{ color: "#6B7280", fontSize: 13, margin: "4px 0 0", fontFamily: "Montserrat, sans-serif" }}>
            Manage the questions and answers shown on /faq
          </p>
        </div>
        <button
          onClick={openAdd}
          style={{
            padding: "9px 18px", background: "#2D5C5A", color: "#fff",
            border: "none", borderRadius: 7, cursor: "pointer",
            fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 13,
          }}
        >
          + Add Question
        </button>
      </div>

      {error && <div style={{ color: "#DC2626", marginBottom: 16, fontSize: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: "#6B7280", fontSize: 14 }}>Loading…</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#F9FAFB" }}>
              <tr>
                {["Question", "Order", "Actions"].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {faqs.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...tdStyle, textAlign: "center", color: "#9CA3AF" }}>
                    No FAQ items yet. Click "+ Add Question" to create one.
                  </td>
                </tr>
              )}
              {faqs.map((f, i) => (
                <tr key={f.id}>
                  <td style={{ ...tdStyle, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 13, color: "#111827", maxWidth: 420 }}>
                    {f.question}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "#6B7280", textAlign: "center" }}>{f.displayOrder}</td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => handleMove(f.id, "up")}
                      disabled={i === 0}
                      title="Move up"
                      style={{ marginRight: 4, padding: "4px 8px", borderRadius: 5, border: "none", cursor: i === 0 ? "not-allowed" : "pointer", background: "#F3F4F6", color: i === 0 ? "#D1D5DB" : "#374151" }}
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      onClick={() => handleMove(f.id, "down")}
                      disabled={i === faqs.length - 1}
                      title="Move down"
                      style={{ marginRight: 10, padding: "4px 8px", borderRadius: 5, border: "none", cursor: i === faqs.length - 1 ? "not-allowed" : "pointer", background: "#F3F4F6", color: i === faqs.length - 1 ? "#D1D5DB" : "#374151" }}
                    >
                      <ArrowDown size={13} />
                    </button>
                    <button onClick={() => openEdit(f)} style={{ marginRight: 6, padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer", background: "#EFF6FF", color: "#1D4ED8", fontSize: 12, fontFamily: "Montserrat, sans-serif" }}>Edit</button>
                    <button
                      onClick={() => handleDelete(f.id)}
                      onBlur={() => { if (deleteConfirm === f.id) setDeleteConfirm(null); }}
                      style={{
                        padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer",
                        background: deleteConfirm === f.id ? "#DC2626" : "#FEE2E2",
                        color: deleteConfirm === f.id ? "#fff" : "#991B1B",
                        fontSize: 12, fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {deleteConfirm === f.id ? "Confirm" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal overlay */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100, padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 32,
            width: "100%", maxWidth: 680, maxHeight: "90vh", overflowY: "auto",
          }}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 16, color: "#111827", margin: 0 }}>
                {editingId ? "Edit Question" : "Add Question"}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6B7280", lineHeight: 1 }}>×</button>
            </div>

            {/* Question */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Question *</label>
              <div style={{ border: "1px solid #D1D5DB", borderRadius: 7, overflow: "hidden" }}>
                <RichTextToolbar field="question" insertFormatting={insertFormatting} />
                <textarea
                  name="question"
                  value={form.question}
                  onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                  onKeyDown={(e) => handleRichTextKeyDown("question", e)}
                  rows={2}
                  style={{ width: "100%", padding: "9px 12px", border: "none", fontSize: 14, fontFamily: "Montserrat, sans-serif", boxSizing: "border-box", color: "#111827", resize: "vertical", outline: "none" }}
                  placeholder="e.g. What does Grassurn Tech do?"
                />
              </div>
            </div>

            {/* Answer */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Answer *</label>
              <div style={{ border: "1px solid #D1D5DB", borderRadius: 7, overflow: "hidden" }}>
                <RichTextToolbar field="answer" insertFormatting={insertFormatting} />
                <textarea
                  name="answer"
                  value={form.answer}
                  onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                  onKeyDown={(e) => handleRichTextKeyDown("answer", e)}
                  rows={6}
                  style={{ width: "100%", padding: "9px 12px", border: "none", fontSize: 14, fontFamily: "Montserrat, sans-serif", boxSizing: "border-box", color: "#111827", resize: "vertical", outline: "none" }}
                  placeholder="Write the answer shown to visitors..."
                />
              </div>
            </div>

            {/* Display order */}
            <div style={{ marginBottom: 28, maxWidth: 200 }}>
              <label style={labelStyle}>Display order</label>
              <input type="number" value={form.displayOrder}
                onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
                style={inputStyle}/>
              <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6, fontFamily: "Montserrat, sans-serif" }}>
                Lower numbers appear first. You can also reorder with the arrows in the table.
              </p>
            </div>

            {/* Modal actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ padding: "9px 18px", border: "1px solid #D1D5DB", borderRadius: 7, background: "#fff", color: "#374151", cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "9px 18px", background: "#2D5C5A", color: "#fff", border: "none", borderRadius: 7, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : editingId ? "Update Question" : "Add Question"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
